// PRD §4.3 — derive the list `preview` (plain-text excerpt) and the read-only
// `contentHtml` snapshot from the authored block array. Mirrors the prototype
// stripHtml / collectPreviewText / serializeBlocksToHtml helpers.

import type { ContentBlock } from '../types/counseling-manual.types';

const PREVIEW_MAX = 200;

// Strip HTML tags and collapse whitespace to a single plain-text line.
export function stripHtml(html: string): string {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return (tmp.textContent ?? '').replace(/\s+/g, ' ').trim();
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Concatenate the visible text across all blocks, then truncate to 200 chars
// with an ellipsis. '' when no block carries any text (table shows '-').
export function collectPreview(blocks: ContentBlock[]): string {
  const text = blocks
    .map((block) => {
      if (block.type === 'text') {
        return [block.title, stripHtml(block.text)].filter(Boolean).join(' ').trim();
      }
      if (block.type === 'image') {
        return [block.title, block.showText ? stripHtml(block.text) : '']
          .filter(Boolean)
          .join(' ')
          .trim();
      }
      return [block.title, block.url ?? '', block.showText ? stripHtml(block.text) : '']
        .filter(Boolean)
        .join(' ')
        .trim();
    })
    .filter(Boolean)
    .join(' ')
    .trim();
  if (!text) return '';
  return text.length > PREVIEW_MAX ? `${text.slice(0, PREVIEW_MAX)}...` : text;
}

// Serialize the block array into a sanitizable HTML snapshot for storage.
// Text-block bodies are already HTML (WYSIWYG); image/video render a marker +
// optional description so the stored snapshot reads sensibly.
export function serializeBlocksToHtml(blocks: ContentBlock[]): string {
  return blocks
    .map((block) => {
      const titleHtml = block.title.trim()
        ? `<h3>${escapeHtml(block.title.trim())}</h3>`
        : '';
      if (block.type === 'text') {
        const body = block.text.trim() || '<p>내용 없음</p>';
        return `${titleHtml}${body}`;
      }
      if (block.type === 'image') {
        const desc = block.showText
          ? `<p>${escapeHtml(block.text.trim())}</p>`
          : '';
        return `${titleHtml}<p>[이미지]</p>${desc}`;
      }
      const url = escapeHtml(block.url?.trim() || '영상 URL 미입력');
      const desc = block.showText ? `<p>${escapeHtml(block.text.trim())}</p>` : '';
      return `${titleHtml}<p>[영상] ${url}</p>${desc}`;
    })
    .join('');
}
